<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { DAY_IDS } from '@/utils/customPlaylistSchedule';

/**
 * The days a schedule repeats on: one round button per day, Sunday first, and "Every day".
 * The model is the day ids, 1 for Sunday to 7 for Saturday, sorted, as delimited_day_ids
 * stores them. The schedule editor and the bulk edit dialog both use it, so a user can only
 * ever pick real days.
 */
const props = withDefaults(
    defineProps<{
        /** Names the group for screen readers. */
        label?: string;
        disabled?: boolean;
        /** Prefix for the test ids, so two pickers on one page stay apart. */
        testIdPrefix?: string;
        /** "sm" fits the seven days in a narrow column, as the bulk edit dialog's value column is. */
        size?: 'default' | 'sm';
    }>(),
    { label: 'Repeat on', disabled: false, testIdPrefix: 'schedule', size: 'default' },
);

const dayIds = defineModel<number[]>({ default: () => [] });

// A copy the clicks build on. Two clicks before the parent re-renders must both count, and the
// model only catches up on that render.
const chosen = ref<number[]>([...dayIds.value]);
watch(dayIds, (value) => {
    chosen.value = [...value];
});

function choose(next: number[]): void {
    chosen.value = next;
    dayIds.value = next;
}

const everyDay = computed(() => chosen.value.length === DAY_IDS.length);
const everyDayId = computed(() => `${props.testIdPrefix}-every-day`);

function toggleDay(dayId: number): void {
    const has = chosen.value.includes(dayId);
    const next = has ? chosen.value.filter((id) => id !== dayId) : [...chosen.value, dayId];
    choose(next.sort((left, right) => left - right));
}

function setEveryDay(value: boolean | 'indeterminate'): void {
    choose(value === true ? DAY_IDS.map((day) => day.id) : []);
}
</script>

<template>
    <div class="flex flex-wrap items-center" :class="size === 'sm' ? 'gap-1' : 'gap-1.5'" role="group" :aria-label="label">
        <Button
            v-for="day in DAY_IDS"
            :key="day.id"
            type="button"
            size="icon"
            :variant="chosen.includes(day.id) ? 'default' : 'outline'"
            class="rounded-full"
            :class="size === 'sm' ? 'size-7 text-xs' : ''"
            :disabled="disabled"
            :aria-pressed="chosen.includes(day.id) ? 'true' : 'false'"
            :aria-label="day.name"
            :title="day.name"
            :data-day-id="day.id"
            :data-testid="`${testIdPrefix}-day`"
            @click="toggleDay(day.id)"
        >
            {{ day.short }}
        </Button>
        <div class="flex items-center gap-1.5" :class="size === 'sm' ? 'ml-1 text-xs' : 'ml-2 text-sm'">
            <Checkbox :id="everyDayId" :model-value="everyDay" :disabled="disabled" :data-testid="`${testIdPrefix}-every-day`" @update:model-value="setEveryDay" />
            <label :for="everyDayId" class="cursor-pointer">Every day</label>
        </div>
    </div>
</template>
